import { useContext } from 'react';

import {
  Stack,
  Typography,
  Box,
  Badge,
  BadgeProps,
  styled,
  useTheme,
  Tooltip,
  Skeleton
} from '@mui/material';

import Accordion from '@/components/accordion';
import { THUMB_SIZE } from '@/constants/mediaDisplay';
import { PostDetailsContext } from '@/contexts/PostDetailsContext';
import { useThumbnail } from '@/hooks/useThumbnail';
import { Posts } from '@/models/posts';
import { msToPrettyTimestamp, msToSeconds } from '@/utils/datesAndTime';
import { getLogosFound, isVideoLogoItem, toLogoKeyword } from '@/utils/logos';

interface LogoThumbnailProps {
  mediaUrl?: string;
  mediaType: 'image' | 'video';
  coordinates: Posts.Coordinates;
  timestamp?: number;
}

const LogoThumbnail = ({
  mediaUrl,
  mediaType,
  coordinates,
  timestamp
}: LogoThumbnailProps) => {
  const { isVideo, hasError, crop, mediaProps } = useThumbnail(
    mediaUrl,
    coordinates,
    mediaType,
    timestamp
  );

  if (!mediaProps.src) {
    return (
      <Skeleton variant="rectangular" width={THUMB_SIZE} height={THUMB_SIZE} />
    );
  }

  if (hasError) {
    return <Box width={THUMB_SIZE} height={THUMB_SIZE} bgcolor="grey.300" />;
  }

  return (
    <>
      {!crop && (
        <Skeleton
          variant="rectangular"
          width={THUMB_SIZE}
          height={THUMB_SIZE}
        />
      )}
      <Box
        width={THUMB_SIZE}
        height={THUMB_SIZE}
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        sx={{ display: crop ? 'flex' : 'none' }}
      >
        <Box sx={crop?.boxSx}>
          <Box
            component={isVideo ? 'video' : 'img'}
            {...mediaProps}
            sx={crop?.mediaSx}
          />
        </Box>
      </Box>
    </>
  );
};

interface LogoSquareProps {
  obj: Posts.LogoItem | Posts.VideoLogoItem;
  mediaUrl?: string;
  mediaType: 'image' | 'video';
  isSelected?: boolean;
  isHighlighted: boolean;
  onClick?: () => void;
}

const LogoSquare = ({
  obj,
  mediaUrl,
  mediaType,
  isSelected,
  isHighlighted,
  onClick
}: LogoSquareProps) => {
  const theme = useTheme();
  const isVideoItem = isVideoLogoItem(obj);
  const logosFound = getLogosFound(obj);
  const timestamp = isVideoItem ? obj.timestamp : undefined;
  const hasMultipleLogos = logosFound.length > 1;

  const border = (() => {
    if (isSelected) return `1px solid ${theme.palette.secondary.main}`;
    if (hasMultipleLogos) return `1px solid ${theme.palette.primary.main}`;
    return 'none';
  })();
  const backgroundColor = (() => {
    if (isSelected) return 'rgba(162, 130, 83, 0.30)';
    if (hasMultipleLogos) return 'rgba(0, 0, 0, 0.30)';
    return 'transparent';
  })();

  const renderCaption = ({ content: { brand } }: Posts.LogoItem) => (
    <Stack flex={1} direction="row" gap={0.5} alignItems="center" width="100%">
      {isVideoItem && (
        <Typography
          variant="caption"
          fontSize={8}
          fontWeight={600}
          flexShrink={0}
        >
          {msToPrettyTimestamp(obj.timestamp)}
        </Typography>
      )}
      <Tooltip title={brand} placement="bottom" arrow>
        <Typography
          variant="caption"
          fontSize={8}
          fontWeight={500}
          noWrap
          borderRadius={1}
          sx={{
            backgroundColor: isHighlighted ? '#bcbaff' : 'none',
            px: 0.2,
            mx: -0.2
          }}
        >
          {brand}
        </Typography>
      </Tooltip>
    </Stack>
  );

  return (
    <Stack
      padding="5px"
      direction="row"
      gap={1.8}
      onClick={onClick}
      sx={{
        border,
        backgroundColor,
        transition: 'all 0.3s ease',
        position: 'relative',
        zIndex: isSelected ? 1 : 0,
        cursor: 'pointer'
      }}
    >
      {logosFound.map((logoItem, i) => (
        <Stack key={i} alignItems="center">
          <LogoThumbnail
            mediaUrl={mediaUrl}
            mediaType={mediaType}
            coordinates={logoItem.coordinates}
            timestamp={timestamp}
          />
          {renderCaption(logoItem)}
        </Stack>
      ))}
    </Stack>
  );
};

type LogoItems = Array<Posts.LogoItem | Posts.VideoLogoItem>;

const isSameItem = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

const countMatchedBrands = (items: LogoItems, brandMatches: string[]) =>
  items
    .flatMap(getLogosFound)
    .filter((logo) => brandMatches.includes(logo.content.brand)).length;

interface LogoStackProps {
  items: LogoItems;
  mediaUrl?: string;
  mediaType: 'image' | 'video';
}

const LogoStack = ({ items, mediaUrl, mediaType }: LogoStackProps) => {
  const {
    selectedKeywords,
    setVideoPlaybackStart,
    setVideoLastClick,
    selectedLogoItems,
    setSelectedLogoItems
  } = useContext(PostDetailsContext);

  if (items.length === 0) {
    return (
      <Typography variant="body2" fontSize={12}>
        No logos found
      </Typography>
    );
  }

  const logoMatchesKeywords = (obj: Posts.LogoItem | Posts.VideoLogoItem) =>
    getLogosFound(obj).some((logo) =>
      selectedKeywords.includes(toLogoKeyword(logo.content.brand))
    );

  const isItemSelected = (item: Posts.LogoItem) =>
    selectedLogoItems.some((selected) => isSameItem(selected, item));

  const onClickSquare = (obj: Posts.LogoItem | Posts.VideoLogoItem) => {
    if (isVideoLogoItem(obj)) {
      const updatedSelectedItems = isItemSelected(obj.logoFound[0])
        ? selectedLogoItems.filter(
            (selected) =>
              !obj.logoFound.some((item) => isSameItem(selected, item))
          )
        : obj.logoFound;

      setSelectedLogoItems(updatedSelectedItems);
      setVideoPlaybackStart(msToSeconds(obj.timestamp));
      setVideoLastClick(new Date().getTime());
      return;
    }

    setSelectedLogoItems(
      isItemSelected(obj)
        ? selectedLogoItems.filter((selected) => !isSameItem(selected, obj))
        : [...selectedLogoItems, obj]
    );
  };

  return (
    <Stack
      gap={2}
      maxHeight="265px"
      direction="row"
      alignSelf="stretch"
      flexWrap="wrap"
      sx={{ overflowY: 'auto' }}
    >
      {items.map((obj, i) => (
        <LogoSquare
          key={i}
          obj={obj}
          mediaUrl={mediaUrl}
          mediaType={mediaType}
          isSelected={getLogosFound(obj).every(isItemSelected)}
          isHighlighted={logoMatchesKeywords(obj)}
          onClick={() => onClickSquare(obj)}
        />
      ))}
    </Stack>
  );
};

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: -15,
    top: 12
  }
}));

interface LogoBadgeProps {
  items: LogoItems;
  brandMatches: string[];
  index: number;
}

const LogoBadge = ({ items, brandMatches, index }: LogoBadgeProps) => {
  const { activeIndex } = useContext(PostDetailsContext);
  const isSelected = activeIndex === index;

  return (
    <StyledBadge
      badgeContent={countMatchedBrands(items, brandMatches)}
      color="primary"
    >
      <Typography
        variant="body2"
        sx={{
          transition: 'all 0.15s ease',
          ...(isSelected && {
            fontWeight: 600,
            textDecoration: 'underline'
          })
        }}
      >{`Item ${index + 1}`}</Typography>
    </StyledBadge>
  );
};

const PostLogos = () => {
  const {
    data,
    logos,
    activeIndex,
    hasLogosResults,
    setActiveIndex,
    setVideoPlaybackStart,
    setSelectedLogoItems
  } = useContext(PostDetailsContext);

  if (!data || !hasLogosResults) return <></>;

  const brandMatches: string[] = data.evaluation?.brandMatches ?? [];

  const renderStack = (index: number) => (
    <LogoStack
      items={logos[index] ?? []}
      mediaUrl={data.media[index]?.mediaPath}
      mediaType={data.media[index]?.mediaType as 'image' | 'video'}
    />
  );

  const handleChange = (index: number) => {
    setActiveIndex(index);
    setVideoPlaybackStart(0);
    setSelectedLogoItems([]);
  };

  if (data.media.length === 1) return renderStack(0);

  return (
    <Stack>
      {data.media.map((mediaItem, index) => (
        <Accordion
          id={mediaItem.mediaPath}
          title={
            <LogoBadge
              items={logos[index] ?? []}
              brandMatches={brandMatches}
              index={index}
            />
          }
          handleChange={() => handleChange(index)}
          isExpanded={index === activeIndex}
          key={mediaItem.mediaPath}
          condensed
        >
          {index === activeIndex && renderStack(index)}
        </Accordion>
      ))}
    </Stack>
  );
};

export default PostLogos;
